import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { HeartHandshake, Plus, Pencil, Trash2, ClipboardList, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { maskCPF, maskRG, maskPhone, maskCEP, onlyDigits, fetchCEP } from "@/lib/masks";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import PageHeader from "@/components/PageHeader";
import SearchBox from "@/components/SearchBox";
import EmptyState from "@/components/EmptyState";
import PhotoUploader from "@/components/PhotoUploader";
import { useCrudList, crudCreate, crudUpdate, crudDelete } from "@/lib/crud";
import { useAuth } from "@/context/AuthContext";

const TIPOS = [
  { value: "em_avaliacao", label: "Em avaliação", color: "bg-[#EDE1F3] text-[#7B569A]" },
  { value: "ativo", label: "Ativo", color: "bg-[#DFF4F5] text-[#3A9CA2]" },
  { value: "lista_espera", label: "Lista de espera", color: "bg-[#FBE1EC] text-[#C4547E]" },
  { value: "inativo", label: "Inativo", color: "bg-slate-100 text-slate-600" },
];

const MODALIDADES = [
  { value: "particular", label: "Particular" },
  { value: "convenio", label: "Convênio" },
  { value: "prefeitura", label: "Prefeitura" },
  { value: "projeto_social", label: "Projeto Social (padrinho / empresa)" },
  { value: "lista_espera", label: "Lista de espera" },
];

const emptyResponsavel = () => ({
  _k: (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `k-${Date.now()}-${Math.random()}`,
  nome: "", cpf: "", parentesco: "", telefone: "", email: "", profissao: "",
});

const emptyForm = {
  nome: "", data_nasc: "", sexo: "", cpf: "", rg: "", tipo_sanguineo: "",
  altura_cm: "", dominancia: "",
  diagnostico: "", cid: "", telefone: "",
  tipo: "em_avaliacao", status: "ativo",
  // endereço do praticante (onde reside)
  endereco: { logradouro: "", numero: "", complemento: "", bairro: "", cidade: "", estado: "", cep: "" },
  // responsáveis (mãe, pai, tutor…)
  responsaveis_inline: [emptyResponsavel()],
  // financeiro / enquadramento (fica por último no form)
  modalidade: "particular",
  patrocinador: "",
  valor_mensal: "", dia_vencimento: "",
  emite_nota_fiscal: false,
  observacoes: "",
};

function calcIdade(iso) {
  if (!iso || iso.length < 10) return null;
  const [y, m, d] = iso.slice(0, 10).split("-").map(Number);
  const nasc = new Date(y, m - 1, d);
  const hoje = new Date();
  let idade = hoje.getFullYear() - nasc.getFullYear();
  const mDiff = hoje.getMonth() - nasc.getMonth();
  if (mDiff < 0 || (mDiff === 0 && hoje.getDate() < nasc.getDate())) idade--;
  return idade;
}

export default function Praticantes() {
  const { user } = useAuth();
  const isRead = user.role === "financeiro"; // financeiro não escreve cadastro básico. Admin, recepcionista e terapeuta podem editar.
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [q, setQ] = useState("");
  const [tipoFilter, setTipoFilter] = useState(() => {
    const t = searchParams.get("tipo");
    return t && ["em_avaliacao", "ativo", "lista_espera", "inativo"].includes(t) ? t : "todos";
  });

  const applyTipoFilter = (v) => {
    setTipoFilter(v);
    // Keep URL in sync so bookmark/share works and shortcut re-clicks stay consistent
    const next = new URLSearchParams(searchParams);
    if (v === "todos") next.delete("tipo");
    else next.set("tipo", v);
    setSearchParams(next, { replace: true });
  };
  const params = { q };
  if (tipoFilter !== "todos") params.tipo = tipoFilter;
  const { items, loading, setItems } = useCrudList("/praticantes", params);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  // React to URL param changes (tipo filter + auto-open novo/edit)
  useEffect(() => {
    const t = searchParams.get("tipo");
    if (t && ["em_avaliacao", "ativo", "lista_espera", "inativo"].includes(t)) {
      setTipoFilter(t);
    }
    if (searchParams.get("novo") === "1") {
      openNew();
      const next = new URLSearchParams(searchParams);
      next.delete("novo");
      setSearchParams(next, { replace: true });
    }
    const editId = searchParams.get("edit");
    if (editId && items.length) {
      const target = items.find((x) => x.id === editId);
      if (target) {
        openEdit(target);
        const next = new URLSearchParams(searchParams);
        next.delete("edit");
        setSearchParams(next, { replace: true });
      }
    }
    // eslint-disable-next-line
  }, [searchParams, items]);

  const openNew = () => {
    setEditing(null);
    setForm({ ...emptyForm });
    setOpen(true);
  };
  const openEdit = (p) => {
    setEditing(p);
    // Legacy: se tinha responsavel (singular), converte em array
    const respArr = Array.isArray(p.responsaveis_inline) && p.responsaveis_inline.length
      ? p.responsaveis_inline.map((r) => ({ ...emptyResponsavel(), ...r }))
      : p.responsavel
        ? [{ ...emptyResponsavel(), ...p.responsavel }]
        : [emptyResponsavel()];
    setForm({
      ...emptyForm, ...p,
      valor_mensal: p.valor_mensal ?? "",
      dia_vencimento: p.dia_vencimento ?? "",
      altura_cm: p.altura_cm ?? "",
      endereco: { ...emptyForm.endereco, ...(p.endereco || p.responsavel?.endereco || {}) },
      responsaveis_inline: respArr,
      modalidade: p.modalidade || "particular",
      patrocinador: p.patrocinador || "",
      emite_nota_fiscal: !!p.emite_nota_fiscal,
    });
    setOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      if (payload.valor_mensal === "") delete payload.valor_mensal;
      else payload.valor_mensal = parseFloat(payload.valor_mensal);
      if (payload.dia_vencimento === "") delete payload.dia_vencimento;
      else payload.dia_vencimento = parseInt(payload.dia_vencimento, 10);
      if (payload.altura_cm === "" || payload.altura_cm == null) delete payload.altura_cm;
      else payload.altura_cm = parseFloat(payload.altura_cm);
      // Filtra responsáveis vazios e remove chave visual `_k`
      payload.responsaveis_inline = (payload.responsaveis_inline || [])
        .filter((r) => r.nome && r.nome.trim() !== "")
        .map(({ _k, ...rest }) => rest);
      // Mantém `responsavel` (singular) por compatibilidade — pega o primeiro
      if (payload.responsaveis_inline.length) payload.responsavel = payload.responsaveis_inline[0];
      if (payload.modalidade !== "projeto_social") payload.patrocinador = "";
      if (editing) {
        const updated = await crudUpdate("/praticantes", editing.id, payload);
        setItems((prev) => prev.map((x) => (x.id === updated.id ? updated : x)));
        toast.success("Praticante atualizado");
        setEditing(null);
        setOpen(false);
      } else {
        const created = await crudCreate("/praticantes", payload);
        setItems((prev) => [created, ...prev]);
        setEditing(created);
        toast.success("Praticante cadastrado");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao salvar");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await crudDelete("/praticantes", deleteId);
      setItems((prev) => prev.filter((x) => x.id !== deleteId));
      toast.success("Praticante excluído");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao excluir");
    } finally {
      setDeleteId(null);
    }
  };

  return (
    <div data-testid="praticantes-page">
      <PageHeader
        icon={HeartHandshake}
        title="Praticantes"
        description="Cadastro básico dos praticantes (crianças e adultos) atendidos no centro."
        actions={
          !isRead && (
            <Button onClick={openNew} data-testid="praticante-new-btn" className="rounded-lg bg-brand-turquoise hover:bg-[#3A9CA2] text-white">
              <Plus className="h-4 w-4 mr-1" /> Novo praticante
            </Button>
          )
        }
      />

      <div className="mb-4 flex flex-col sm:flex-row gap-3">
        <SearchBox value={q} onChange={setQ} placeholder="Buscar por nome, CPF, responsável ou diagnóstico…" testId="praticantes-search" />
        <Tabs value={tipoFilter} onValueChange={applyTipoFilter}>
          <TabsList>
            <TabsTrigger value="todos" data-testid="tipo-tab-todos">Todos</TabsTrigger>
            <TabsTrigger value="ativo" data-testid="tipo-tab-ativo">Ativos</TabsTrigger>
            <TabsTrigger value="em_avaliacao" data-testid="tipo-tab-avaliacao">Em avaliação</TabsTrigger>
            <TabsTrigger value="lista_espera" data-testid="tipo-tab-lista-espera">Lista de espera</TabsTrigger>
            <TabsTrigger value="inativo" data-testid="tipo-tab-inativo">Inativos</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      <Card className="border-border shadow-none rounded-xl bg-white">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-10 text-center text-muted-foreground">Carregando…</div>
          ) : items.length === 0 ? (
            <div className="p-6">
              <EmptyState
                icon={HeartHandshake}
                title="Nenhum praticante cadastrado"
                description="Comece adicionando o primeiro praticante do centro."
                action={!isRead && <Button onClick={openNew} data-testid="praticante-new-empty-btn" className="bg-brand-turquoise hover:bg-[#3A9CA2] text-white"><Plus className="h-4 w-4 mr-1" />Novo praticante</Button>}
              />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nome</TableHead>
                  <TableHead>Idade</TableHead>
                  <TableHead>Responsável</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead className="w-32"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((p) => {
                  const tipoOpt = TIPOS.find((t) => t.value === p.tipo) || TIPOS[0];
                  const idade = calcIdade(p.data_nasc);
                  return (
                    <TableRow key={p.id} data-testid={`praticante-row-${p.id}`}>
                      <TableCell className="font-medium">
                        <button
                          type="button"
                          onClick={() => navigate(`/praticantes/${p.id}`)}
                          data-testid={`praticante-open-${p.id}`}
                          className="flex items-center gap-3 hover:text-brand-turquoise transition-colors text-left"
                        >
                          {p.foto_url ? (
                            <img src={`${process.env.REACT_APP_BACKEND_URL}${p.foto_url}`} alt="" className="h-9 w-9 rounded-full object-cover border border-border" />
                          ) : (
                            <div className="h-9 w-9 rounded-full bg-secondary flex items-center justify-center text-xs font-semibold text-brand-turquoise">
                              {(p.nome || "?").slice(0, 2).toUpperCase()}
                            </div>
                          )}
                          <span className="hover:underline">{p.nome}</span>
                        </button>
                      </TableCell>
                      <TableCell>{idade != null ? `${idade} anos` : "—"}</TableCell>
                      <TableCell className="text-sm">{p.responsaveis_inline?.[0]?.nome || p.responsavel?.nome || "—"}</TableCell>
                      <TableCell>
                        <span className={`text-xs px-2 py-1 rounded-full ${tipoOpt.color}`}>{tipoOpt.label}</span>
                      </TableCell>
                      <TableCell>
                        <div className="flex justify-end gap-1">
                          {(user.role === "admin" || user.role === "terapeuta") && (
                            <Button size="icon" variant="ghost" onClick={() => navigate(`/praticantes/${p.id}/ficha`)} title="Ficha de avaliação" data-testid={`praticante-ficha-${p.id}`}>
                              <ClipboardList className="h-4 w-4 text-brand-lilac" />
                            </Button>
                          )}
                          {!isRead && (
                            <>
                              <Button size="icon" variant="ghost" onClick={() => openEdit(p)} data-testid={`praticante-edit-${p.id}`}>
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <Button size="icon" variant="ghost" onClick={() => setDeleteId(p.id)} data-testid={`praticante-delete-${p.id}`}>
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? "Editar praticante" : "Novo praticante"}</DialogTitle>
            <DialogDescription>
              Cadastro básico do praticante. A ficha de avaliação clínica é preenchida separadamente pelo terapeuta.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-6">
            {editing && (
              <PhotoUploader
                entity="praticantes"
                entityId={editing.id}
                currentUrl={editing.foto_url}
                onUploaded={(url) => {
                  setEditing({ ...editing, foto_url: url });
                  setItems((prev) => prev.map((x) => (x.id === editing.id ? { ...x, foto_url: url } : x)));
                }}
              />
            )}

            {/* Dados pessoais */}
            <section>
              <h3 className="font-heading font-semibold text-sm uppercase tracking-wider text-brand-turquoise mb-3">
                Dados pessoais
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <F label="Nome completo" req><Input required value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} data-testid="praticante-form-nome" /></F>
                <F label="Data de nascimento"><Input type="date" value={form.data_nasc || ""} onChange={(e) => setForm({ ...form, data_nasc: e.target.value })} data-testid="praticante-form-datanasc" /></F>
                <F label="Sexo">
                  <Select value={form.sexo || ""} onValueChange={(v) => setForm({ ...form, sexo: v })}>
                    <SelectTrigger><SelectValue placeholder="Selecionar" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="M">Masculino</SelectItem>
                      <SelectItem value="F">Feminino</SelectItem>
                      <SelectItem value="Outro">Outro</SelectItem>
                    </SelectContent>
                  </Select>
                </F>
                <F label="CPF"><Input placeholder="000.000.000-00" value={form.cpf || ""} onChange={(e) => setForm({ ...form, cpf: maskCPF(e.target.value) })} /></F>
                <F label="RG"><Input placeholder="00.000.000-0" value={form.rg || ""} onChange={(e) => setForm({ ...form, rg: maskRG(e.target.value) })} /></F>
                <F label="Telefone do praticante"><Input placeholder="(00) 00000-0000" value={form.telefone || ""} onChange={(e) => setForm({ ...form, telefone: maskPhone(e.target.value) })} /></F>
              </div>
            </section>

            {/* Endereço do praticante (onde reside) */}
            <section>
              <h3 className="font-heading font-semibold text-sm uppercase tracking-wider text-brand-turquoise mb-3">
                Endereço do praticante <span className="text-muted-foreground normal-case text-xs font-normal">(onde a criança reside)</span>
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
                <div className="md:col-span-2"><F label="CEP"><Input placeholder="00000-000" value={form.endereco?.cep || ""} onChange={async (e) => {
                  const masked = maskCEP(e.target.value);
                  setForm((f) => ({ ...f, endereco: { ...f.endereco, cep: masked } }));
                  if (onlyDigits(masked).length === 8) {
                    const r = await fetchCEP(masked);
                    if (r) setForm((f) => ({ ...f, endereco: { ...f.endereco, logradouro: r.logradouro || f.endereco?.logradouro || "", bairro: r.bairro || f.endereco?.bairro || "", cidade: r.cidade || f.endereco?.cidade || "", estado: r.estado || f.endereco?.estado || "" } }));
                  }
                }} /></F></div>
                <div className="md:col-span-4"><F label="Logradouro"><Input value={form.endereco?.logradouro || ""} onChange={(e) => setForm({ ...form, endereco: { ...form.endereco, logradouro: e.target.value } })} data-testid="praticante-form-endereco-log" /></F></div>
                <div><F label="Nº"><Input value={form.endereco?.numero || ""} onChange={(e) => setForm({ ...form, endereco: { ...form.endereco, numero: e.target.value } })} /></F></div>
                <div><F label="Complemento"><Input value={form.endereco?.complemento || ""} onChange={(e) => setForm({ ...form, endereco: { ...form.endereco, complemento: e.target.value } })} /></F></div>
                <div className="md:col-span-2"><F label="Bairro"><Input value={form.endereco?.bairro || ""} onChange={(e) => setForm({ ...form, endereco: { ...form.endereco, bairro: e.target.value } })} /></F></div>
                <div className="md:col-span-2"><F label="Cidade"><Input value={form.endereco?.cidade || ""} onChange={(e) => setForm({ ...form, endereco: { ...form.endereco, cidade: e.target.value } })} /></F></div>
                <div><F label="UF"><Input maxLength={2} value={form.endereco?.estado || ""} onChange={(e) => setForm({ ...form, endereco: { ...form.endereco, estado: e.target.value.toUpperCase() } })} /></F></div>
              </div>
            </section>

            {/* Responsáveis (múltiplos - pai/mãe/tutor) */}
            <section>
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-heading font-semibold text-sm uppercase tracking-wider text-brand-turquoise">
                  Responsáveis <span className="text-muted-foreground normal-case text-xs font-normal">(pai, mãe, tutor)</span>
                </h3>
                <Button
                  type="button" size="sm" variant="outline"
                  onClick={() => setForm({ ...form, responsaveis_inline: [...form.responsaveis_inline, emptyResponsavel()] })}
                  data-testid="praticante-add-responsavel"
                >
                  <Plus className="h-3 w-3 mr-1" /> Adicionar responsável
                </Button>
              </div>
              <div className="space-y-4">
                {form.responsaveis_inline.map((r, i) => (
                  <div key={r._k || i} className="rounded-lg border border-border bg-slate-50/50 p-4 space-y-3" data-testid={`praticante-responsavel-${i}`}>
                    <div className="flex items-center justify-between">
                      <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Responsável {i + 1}</p>
                      {form.responsaveis_inline.length > 1 && (
                        <Button type="button" size="icon" variant="ghost" onClick={() => setForm({ ...form, responsaveis_inline: form.responsaveis_inline.filter((_, idx) => idx !== i) })} data-testid={`praticante-remove-resp-${i}`}>
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      )}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <F label="Nome" req={i === 0}>
                        <Input value={r.nome} onChange={(e) => setForm({ ...form, responsaveis_inline: form.responsaveis_inline.map((x, idx) => idx === i ? { ...x, nome: e.target.value } : x) })} data-testid={`resp-${i}-nome`} />
                      </F>
                      <F label="Parentesco">
                        <Select value={r.parentesco || ""} onValueChange={(v) => setForm({ ...form, responsaveis_inline: form.responsaveis_inline.map((x, idx) => idx === i ? { ...x, parentesco: v } : x) })}>
                          <SelectTrigger><SelectValue placeholder="Selecionar" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="mae">Mãe</SelectItem>
                            <SelectItem value="pai">Pai</SelectItem>
                            <SelectItem value="avo">Avó / Avô</SelectItem>
                            <SelectItem value="tio">Tio(a)</SelectItem>
                            <SelectItem value="tutor">Tutor(a) legal</SelectItem>
                            <SelectItem value="cuidador">Cuidador(a)</SelectItem>
                            <SelectItem value="outro">Outro</SelectItem>
                          </SelectContent>
                        </Select>
                      </F>
                      <F label="CPF">
                        <Input placeholder="000.000.000-00" value={r.cpf} onChange={(e) => setForm({ ...form, responsaveis_inline: form.responsaveis_inline.map((x, idx) => idx === i ? { ...x, cpf: maskCPF(e.target.value) } : x) })} />
                      </F>
                      <F label="Profissão">
                        <Input value={r.profissao} placeholder="Ex: Professora, Engenheiro…" onChange={(e) => setForm({ ...form, responsaveis_inline: form.responsaveis_inline.map((x, idx) => idx === i ? { ...x, profissao: e.target.value } : x) })} data-testid={`resp-${i}-profissao`} />
                      </F>
                      <F label="Telefone">
                        <Input placeholder="(00) 00000-0000" value={r.telefone} onChange={(e) => setForm({ ...form, responsaveis_inline: form.responsaveis_inline.map((x, idx) => idx === i ? { ...x, telefone: maskPhone(e.target.value) } : x) })} />
                      </F>
                      <F label="E-mail">
                        <Input type="email" value={r.email} onChange={(e) => setForm({ ...form, responsaveis_inline: form.responsaveis_inline.map((x, idx) => idx === i ? { ...x, email: e.target.value } : x) })} data-testid={`resp-${i}-email`} />
                      </F>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <F label="Observações">
              <Textarea rows={3} value={form.observacoes || ""} onChange={(e) => setForm({ ...form, observacoes: e.target.value })} />
            </F>

            {/* Enquadramento financeiro (por último) */}
            <section className="rounded-xl border-2 border-brand-turquoise/30 bg-[#F0FBFC]/40 p-4">
              <h3 className="font-heading font-semibold text-sm uppercase tracking-wider text-brand-turquoise mb-3">
                Enquadramento e financeiro
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <F label="Situação do praticante" req>
                  <Select value={form.tipo || "em_avaliacao"} onValueChange={(v) => setForm({ ...form, tipo: v })}>
                    <SelectTrigger data-testid="praticante-form-tipo"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="em_avaliacao">Em avaliação (novo)</SelectItem>
                      <SelectItem value="ativo">Ativo (em acompanhamento)</SelectItem>
                      <SelectItem value="lista_espera">Lista de espera</SelectItem>
                      <SelectItem value="inativo">Inativo</SelectItem>
                    </SelectContent>
                  </Select>
                </F>
                <F label="Modalidade de atendimento" req>
                  <Select value={form.modalidade || "particular"} onValueChange={(v) => setForm({ ...form, modalidade: v })}>
                    <SelectTrigger data-testid="praticante-form-modalidade"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {MODALIDADES.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </F>
                {form.modalidade === "projeto_social" && (
                  <F label="Empresa / Instituição patrocinadora" className="md:col-span-2">
                    <Input value={form.patrocinador} placeholder="Ex: Empresa XYZ · Padrinho Fulano" onChange={(e) => setForm({ ...form, patrocinador: e.target.value })} data-testid="praticante-form-patrocinador" />
                  </F>
                )}
                <F label="Valor mensal (R$)"><Input type="number" step="0.01" value={form.valor_mensal} onChange={(e) => setForm({ ...form, valor_mensal: e.target.value })} placeholder="Ex: 500.00" data-testid="praticante-form-valor" /></F>
                <F label="Dia de vencimento (1-28)"><Input type="number" min="1" max="28" value={form.dia_vencimento} onChange={(e) => setForm({ ...form, dia_vencimento: e.target.value })} data-testid="praticante-form-vencimento" /></F>
                <div className="md:col-span-2 flex items-start gap-2 rounded-lg border border-border bg-white p-3">
                  <Checkbox
                    id="emite-nf"
                    checked={form.emite_nota_fiscal}
                    onCheckedChange={(v) => setForm({ ...form, emite_nota_fiscal: !!v })}
                    data-testid="praticante-form-nota-fiscal"
                  />
                  <label htmlFor="emite-nf" className="text-sm cursor-pointer">
                    <span className="font-medium text-slate-700">Deseja emitir nota fiscal para este praticante?</span>
                    <p className="text-xs text-muted-foreground mt-0.5">Quando marcado, ao dar baixa em pagamentos aparecerá o lembrete de emissão de NF.</p>
                  </label>
                </div>
              </div>
            </section>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Fechar</Button>
              <Button type="submit" disabled={saving} data-testid="praticante-form-submit" className="bg-brand-turquoise hover:bg-[#3A9CA2] text-white">
                {saving ? "Salvando…" : editing ? "Salvar alterações" : "Cadastrar"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir praticante?</AlertDialogTitle>
            <AlertDialogDescription>Esta ação não pode ser desfeita. A ficha de avaliação também será removida.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction data-testid="praticante-delete-confirm" onClick={handleDelete} className="bg-destructive hover:bg-destructive/90">Excluir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function F({ label, req, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs uppercase tracking-wide text-muted-foreground">
        {label}{req && <span className="text-destructive"> *</span>}
      </Label>
      {children}
    </div>
  );
}
